import React from 'react';
import {
  compose,
  withProps,
  mapProps,
  setDisplayName,
  withHandlers,
  withState,
} from 'recompose';
import { FormattedMessage } from 'react-intl';
import TextButton, {
  Against as TextButtonAgainst,
  Size as TextButtonSize,
} from '@ux/text-button';
import Button, {
  Kind as ButtonKind,
  Against as ButtonAgainst,
} from '@ux/button';
import Panel from '@ux/panel';
import Glyph from '@ux/glyphs';
import { Content } from '@ux/typeface';
import { withStyles } from '@ux/style-utils';
import * as r from 'ramda';
import { AvailabilityStatus } from '../../../core/constants';
import stylesheet from './Taken.stylesheet';

export { AvailabilityStatus };

const ActionButton = compose(
  setDisplayName('ActionButton'),
  withHandlers({
    onClick: (props) => () => {
      props.onClick();
      props.onActionClick(AvailabilityStatus.VERIFYING);
      setTimeout(function() {
        props.onActionClick(AvailabilityStatus.AVAILABLE);
      }, 1500);
    },
  }),
  mapProps(r.omit([
    'onActionClick',
  ])),
)(TextButton);

export const PureTakenOptions = r.cond([
  [ r.propSatisfies(r.equals(AvailabilityStatus.VERIFYING), 'actionStatus'), ({
    styles,
    intlNamespace,
  }) => (
    <div style={styles.message}>
      <FormattedMessage id={`dst.${intlNamespace}.verifying`}/>
    </div>
  ) ],
  [ r.propSatisfies(r.equals(AvailabilityStatus.AVAILABLE), 'actionStatus'), ({
    styles,
    intlNamespace,
  }) => (
    <div style={styles.message}>
      <FormattedMessage id={`dst.${intlNamespace}.available`}/>
      {' '}
      <span style={styles.icon}>
        <Glyph
          icon="circleTick"
          color={styles.icon.color}
          size={styles.icon.size}
        />
      </span>
    </div>
  ) ],
  [ r.T, ({
    theme,
    styles,
    intlNamespace,
    onTransferDomainClick,
    onBackorderDomainClick,
    fqdn,
    message,
    onActionClick,
  }) => (
    <div
      data-test-id="actions"
      style={styles.message}
    >
      <FormattedMessage
        id={message}
        values={{
          transfer: (
            <ActionButton
              theme={theme}
              type="button"
              size={TextButtonSize.LG}
              against={TextButtonAgainst.NEUTRAL}
              onClick={onTransferDomainClick}
              onActionClick={onActionClick}
              key={fqdn}
            >
              <FormattedMessage id={`dst.${intlNamespace}.transfer`}/>
            </ActionButton>
          ),
          backorder: (
            <ActionButton
              theme={theme}
              type="button"
              size={TextButtonSize.LG}
              against={TextButtonAgainst.NEUTRAL}
              onClick={onBackorderDomainClick}
              onActionClick={onActionClick}
            >
              <FormattedMessage id={`dst.${intlNamespace}.backorder`}/>
            </ActionButton>
          ),
        }}
      />
    </div>
  ) ],
]);

export const TakenOptions = compose(
  setDisplayName('TakenOptions')
)(PureTakenOptions);

export const PureExactMatchTaken = ({
  theme,
  fqdn,
  styles,
  isMinMd,
  message,
  canTransfer,
  canBackorder,
  canNegotiate,
  handleTransferDomainClick,
  handleBackorderDomainClick,
  handleNegotiateDomainClick,
  onActionClick,
  actionStatus,
}) => (
  <Panel
    theme={theme}
    style={styles.root}
  >
    <Content
      component="div"
      theme={theme}
    >
      <div
        style={styles.body}
        className="d-flex"
      >
        <div style={styles.text}>
          <FormattedMessage
            id="dst.exactMatch.v2.notFound.text"
            values={{
              domain: (
                <strong style={styles.fqdn}>{fqdn}</strong>
              ),
            }}
          />
        </div>
        <div style={styles.options}>
          <TakenOptions
            fqdn={fqdn}
            theme={theme}
            message={message}
            intlNamespace="exactMatch.v3"
            canTransfer={canTransfer}
            canBackorder={canBackorder}
            actionStatus={actionStatus}
            styles={styles}
            isMinMd={isMinMd}
            onActionClick={onActionClick}
            onTransferDomainClick={handleTransferDomainClick}
            onBackorderDomainClick={handleBackorderDomainClick}
          />
        </div>
      </div>
      <If condition={canNegotiate}>
        <div
          data-test-id="afternic"
          style={styles.negotiate}
        >
          <div>
            <b>
              <FormattedMessage
                id="dst.exactMatch.v3.notFound.negotiate.title"
              />
            </b>
            {' '}
            <FormattedMessage id="dst.exactMatch.v3.notFound.negotiate.text"/>
          </div>
          <div style={styles.button}>
            <Button
              kind={ButtonKind.PRIMARY}
              against={ButtonAgainst.PRIMARY}
              theme={theme}
              onClick={handleNegotiateDomainClick}
            >
              <FormattedMessage
                id="dst.exactMatch.v3.notFound.negotiate.button"
              />
            </Button>
          </div>
        </div>
      </If>
    </Content>
  </Panel>
);

export const enhance = compose(
  setDisplayName('ExactMatchTaken'),
  //force state reload so transfer button reappears
  withProps((ownerProps) => ({
    key: ownerProps.fqdn,
  })),
  withState('actionStatus', 'setActionStatus'),
  withHandlers({
    onActionClick: ({
      setActionStatus,
    }) => (status) => setActionStatus(status),
  }),
  withProps((ownerProps) => ({
    message: r.cond([
      [
        () => ownerProps.canTransfer && ownerProps.canNegotiate && ownerProps.canBackorder,
        r.always('dst.exactMatch.v2.notFound.negotiateOrBackorderOrTransfer'),
      ],
      [
        () => r.and(ownerProps.canTransfer, ownerProps.canBackorder),
        r.always('dst.exactMatch.v3.notFound.transferOrBackorder'),
      ],
      [
        r.propEq('canBackorder', true),
        r.always('dst.exactMatch.v3.notFound.backorder'),
      ],
      [
        r.propEq('canTransfer', true),
        r.always('dst.exactMatch.v3.notFound.transfer'),
      ],
      [
        r.T,
        r.always('dst.exactMatch.v3.notFound.none'),
      ],
    ])(ownerProps),
  })),
  withStyles(stylesheet),
);

export default enhance(PureExactMatchTaken);
